import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// scopeKey only partitions local state; authorization remains entirely server-side.
export default function useOrdersPolling({ apiBaseUrl, status = 'all', scopeKey }) {
  const identity = JSON.stringify([apiBaseUrl, scopeKey]);
  const key = JSON.stringify([identity, status]);
  const sessionRef = useRef(null);
  const [snapshot, setSnapshot] = useState({ key, orders: [], loaded: false, error: null });
  const current = snapshot.key === key ? snapshot : { orders: [], loaded: false, error: null };

  useEffect(() => {
    const session = { key, identity, active: true, request: null, generation: 0, lastReturn: -Infinity };
    sessionRef.current = session;
    setSnapshot({ key, orders: [], loaded: false, error: null });

    session.cancel = () => {
      session.generation += 1;
      session.request?.abort();
      session.request = null;
    };
    session.fetch = async () => {
      if (!session.active || session.request) return;
      const controller = new AbortController();
      const generation = ++session.generation;
      session.request = controller;
      try {
        const suffix = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`;
        const { data } = await axios.get(`${apiBaseUrl}/orders${suffix}`, {
          withCredentials: true, signal: controller.signal, timeout: 15000,
        });
        if (!session.active || generation !== session.generation) return;
        if (!Array.isArray(data)) throw new Error('Invalid orders response');
        setSnapshot({ key, orders: data, loaded: true, error: null });
      } catch (error) {
        if (!session.active || generation !== session.generation || controller.signal.aborted) return;
        setSnapshot(previous => ({
          ...(previous.key === key ? previous : { key, orders: [], loaded: false }),
          error,
        }));
      } finally {
        if (generation === session.generation) session.request = null;
      }
    };
    const onReturn = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - session.lastReturn < 500) return;
      session.lastReturn = now;
      session.fetch();
    };
    session.fetch();
    const timer = setInterval(session.fetch, 10000);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      session.active = false;
      session.cancel();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [apiBaseUrl, status, key, identity]);

  const refresh = useCallback(() => {
    const session = sessionRef.current;
    if (session?.active && session.key === key) return session.fetch();
  }, [key]);

  // A completed mutation supersedes any GET that began before it.
  const refreshAfterMutation = useCallback((updateOrders) => {
    const session = sessionRef.current;
    if (!session?.active || session.identity !== identity) return;
    session.cancel();
    if (updateOrders) {
      setSnapshot(previous => previous.key === session.key
        ? { ...previous, orders: updateOrders(previous.orders) } : previous);
    }
    return session.fetch();
  }, [identity]);

  return {
    orders: current.orders, hasLoaded: current.loaded,
    initialLoading: !current.loaded && !current.error,
    initialError: !current.loaded ? current.error : null,
    refreshError: current.loaded ? current.error : null,
    refresh, refreshAfterMutation,
  };
}
